import type { GiantId } from '../../domain/ids/ids';
import { getLevelVisualAssetUrlsForGiants } from '../screens/level_screen/gameplay/levelVisualAssets';
import { preloadImages, type PreloadImagesResult } from './preloadImages';

const readyGiants = new Set<GiantId>();
const inflightByGiant = new Map<GiantId, Promise<void>>();
let sessionPreloadInflight: Promise<PreloadImagesResult> | null = null;

export type SessionAssetsStatus = 'pending' | 'loading' | 'ready';

export type PreloadSessionLevelAssetsOptions = {
  /** Max not-yet-ready giants to warm. Default 1 — never warm the full daily pack. */
  limit?: number;
};

let sessionAssetsStatus: SessionAssetsStatus = 'pending';

export function getSessionAssetsStatus(): SessionAssetsStatus {
  return sessionAssetsStatus;
}

export function isGiantLevelAssetsReady(giantId: GiantId): boolean {
  return readyGiants.has(giantId);
}

/** Test helper. */
export function resetSessionLevelAssetState(): void {
  readyGiants.clear();
  inflightByGiant.clear();
  sessionPreloadInflight = null;
  sessionAssetsStatus = 'pending';
}

function markGiantsReady(giantIds: readonly GiantId[]): void {
  for (const id of giantIds) {
    readyGiants.add(id);
  }
}

/**
 * Speculative preload of level art for up to `limit` giants (default 1).
 * Use for the next unfinished pool giant — never warm the full daily pack.
 */
export async function preloadSessionLevelAssets(
  giantIds: readonly GiantId[],
  options: PreloadSessionLevelAssetsOptions = {},
): Promise<PreloadImagesResult> {
  const limit = Math.max(0, options.limit ?? 1);
  const unique = [...new Set(giantIds)];
  const pending = unique.filter((id) => !readyGiants.has(id)).slice(0, limit);

  if (pending.length === 0) {
    sessionAssetsStatus = 'ready';
    return { loaded: 0, total: 0, failedUrls: [] };
  }

  if (sessionPreloadInflight != null) {
    await sessionPreloadInflight;
    const stillPending = pending.filter((id) => !readyGiants.has(id));
    if (stillPending.length === 0) {
      sessionAssetsStatus = 'ready';
      return { loaded: 0, total: 0, failedUrls: [] };
    }
  }

  sessionAssetsStatus = 'loading';

  const run = (async (): Promise<PreloadImagesResult> => {
    if (process.env.NODE_ENV === 'test') {
      markGiantsReady(pending);
      sessionAssetsStatus = 'ready';
      return { loaded: 0, total: 0, failedUrls: [] };
    }
    const urls = getLevelVisualAssetUrlsForGiants(pending);
    const result = await preloadImages(urls, { concurrency: 6 });
    markGiantsReady(pending);
    sessionAssetsStatus = 'ready';
    return result;
  })();

  sessionPreloadInflight = run;
  try {
    return await run;
  } finally {
    if (sessionPreloadInflight === run) {
      sessionPreloadInflight = null;
    }
  }
}

/**
 * Ensures a single giant's level visuals are preloaded before gameplay.
 * Resolves even on failure so the level can still mount and load via img.
 */
export async function ensureGiantLevelAssets(giantId: GiantId): Promise<void> {
  if (readyGiants.has(giantId)) {
    return;
  }

  const existing = inflightByGiant.get(giantId);
  if (existing != null) {
    await existing;
    return;
  }

  const work = (async () => {
    if (process.env.NODE_ENV === 'test') {
      readyGiants.add(giantId);
      return;
    }
    try {
      await preloadImages(getLevelVisualAssetUrlsForGiants([giantId]), {
        concurrency: 4,
      });
    } catch (e) {
      console.warn('[assets] ensureGiantLevelAssets failed', giantId, e);
    } finally {
      readyGiants.add(giantId);
    }
  })();

  inflightByGiant.set(giantId, work);
  try {
    await work;
  } finally {
    inflightByGiant.delete(giantId);
  }
}
